import type { BillData } from '../types';

export interface EffectiveConsumptionResult {
  morningConsumptionPct: number;
  factor: number;
  baseYearlyConsumption: number;
  effectiveYearlyConsumption: number;
  baseMonthlyConsumption: number[];
  effectiveMonthlyConsumption: number[];
}

function sanitizeMonthValue(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 0;
  return value > 0 ? value : 0;
}

function isValidMonthlyArray(values: unknown): values is number[] {
  return (
    Array.isArray(values) &&
    values.length === 12 &&
    values.some((v) => typeof v === 'number' && Number.isFinite(v) && v > 0)
  );
}

export function normalizeMorningConsumptionPct(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 100;
  if (value < 0) return 0;
  if (value > 100) return 100;
  return value;
}

export function getEffectiveConsumption(
  billData: BillData | null | undefined,
  fallbackYearlyConsumption: number = 12000
): EffectiveConsumptionResult {
  const monthlyFromBill = isValidMonthlyArray(billData?.monthlyConsumptionArray)
    ? billData!.monthlyConsumptionArray!.map(sanitizeMonthValue)
    : null;

  const yearlyFromMonthly = monthlyFromBill
    ? monthlyFromBill.reduce((sum, value) => sum + value, 0)
    : 0;

  const yearlyFromBill =
    typeof billData?.yearlyConsumption === 'number' &&
    Number.isFinite(billData.yearlyConsumption) &&
    billData.yearlyConsumption > 0
      ? billData.yearlyConsumption
      : 0;

  const yearlyFromMonthlyUsage =
    typeof billData?.monthlyUsage === 'number' &&
    Number.isFinite(billData.monthlyUsage) &&
    billData.monthlyUsage > 0
      ? billData.monthlyUsage * 12
      : 0;

  const baseYearlyConsumption =
    yearlyFromMonthly ||
    yearlyFromBill ||
    yearlyFromMonthlyUsage ||
    (Number.isFinite(fallbackYearlyConsumption) && fallbackYearlyConsumption >= 0 ? fallbackYearlyConsumption : 12000);

  const baseMonthlyConsumption =
    monthlyFromBill ?? new Array(12).fill(baseYearlyConsumption / 12);

  const morningConsumptionPct = normalizeMorningConsumptionPct(billData?.morningConsumptionPct);
  const factor = morningConsumptionPct / 100;

  const effectiveMonthlyConsumption = baseMonthlyConsumption.map((value) => value * factor);
  const effectiveYearlyConsumption = baseYearlyConsumption * factor;

  return {
    morningConsumptionPct,
    factor,
    baseYearlyConsumption,
    effectiveYearlyConsumption,
    baseMonthlyConsumption,
    effectiveMonthlyConsumption,
  };
}
